import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DeleteModal from "../../components/Common/DeleteModal";
import { baseUrl } from "../../Network";
import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import { HTML_SANDBOX, prepareHtmlForPreview } from "./htmlPreview";

const MAX_MB = 10; // Cloudinary free plan limit; keep in sync with the API (services/resourceFile.js)
const ACCEPT = ".pdf,.html,.htm";

const emptyForm = () => ({ name: "", sortOrder: "", isVisible: true, file: null });

const fileTypeOf = (name) => {
  const ext = String(name || "").toLowerCase().split(".").pop();
  if (ext === "pdf") return "pdf";
  if (ext === "html" || ext === "htm") return "html";
  return null;
};

const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

// The database can return booleans as 1/0, so don't compare with true/false directly.
const isOn = (value) => value === true || value === 1 || value === "1" || value === "true";

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const ManageResource = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // resource being edited, or null when adding
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saveError, setSaveError] = useState("");

  const [toDelete, setToDelete] = useState(null);

  const [preview, setPreview] = useState(null); // { resource, url?, html?, loading, error }

  useEffect(() => {
    fetchResources();
  }, []);

  // Free the PDF preview's object URL when the preview closes or changes.
  useEffect(() => () => preview?.url && URL.revokeObjectURL(preview.url), [preview?.url]);

  const fetchResources = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(`${baseUrl}/admin/resource/all`);
      setResources(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      handleAuthError(error);
      setLoadError(errorMessage(error, "Could not load resources"));
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? resources.filter((r) => `${r.name} ${r.originalName || ""}`.toLowerCase().includes(q)) : resources;
  }, [resources, search]);

  const nextOrder = () => (resources.length ? Math.max(...resources.map((r) => Number(r.sortOrder) || 0)) + 1 : 1);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), sortOrder: String(nextOrder()) });
    setErrors({});
    setSaveError("");
    setProgress(0);
    setModalOpen(true);
  };

  const openEdit = (resource) => {
    setEditing(resource);
    setForm({ name: resource.name, sortOrder: String(resource.sortOrder), isVisible: isOn(resource.isVisible), file: null });
    setErrors({});
    setSaveError("");
    setProgress(0);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    else if (form.name.trim().length > 200) next.name = "Name must be 200 characters or fewer";
    if (!/^\d+$/.test(String(form.sortOrder).trim())) next.sortOrder = "Order must be a whole number (0 or more)";
    if (!editing && !form.file) next.file = "Choose a PDF or HTML file";
    if (form.file) {
      if (!fileTypeOf(form.file.name)) next.file = "Only PDF and HTML files are accepted";
      else if (form.file.size > MAX_MB * 1024 * 1024) next.file = `File must be ${MAX_MB}MB or smaller`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const data = new FormData();
    data.append("name", form.name.trim());
    data.append("sortOrder", String(form.sortOrder).trim());
    data.append("isVisible", form.isVisible ? "true" : "false");
    if (form.file) data.append("file", form.file);

    setSaving(true);
    setSaveError("");
    setProgress(0);
    try {
      const config = {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => event.total && setProgress(Math.round((event.loaded / event.total) * 100)),
      };
      const response = editing
        ? await axios.put(`${baseUrl}/admin/resource/edit/${editing.id}`, data, config)
        : await axios.post(`${baseUrl}/admin/resource/create`, data, config);
      if (!response.data?.status) throw new Error(response.data?.message || "Could not save the resource");
      toast.success(editing ? "Resource updated" : "Resource added");
      setModalOpen(false);
      fetchResources();
    } catch (error) {
      handleAuthError(error);
      const message = errorMessage(error, "Could not save the resource");
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const resource = toDelete;
    setToDelete(null);
    if (!resource) return;
    try {
      await axios.delete(`${baseUrl}/admin/resource/${resource.id}`);
      toast.success("Resource deleted");
      fetchResources();
    } catch (error) {
      handleAuthError(error);
      toast.error(errorMessage(error, "Could not delete the resource"));
    }
  };

  const toggleVisible = async (resource) => {
    const data = new FormData();
    data.append("isVisible", isOn(resource.isVisible) ? "false" : "true");
    try {
      await axios.put(`${baseUrl}/admin/resource/edit/${resource.id}`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isOn(resource.isVisible) ? "Resource hidden from teachers" : "Teachers can now see this resource");
      fetchResources();
    } catch (error) {
      handleAuthError(error);
      toast.error(errorMessage(error, "Could not update the resource"));
    }
  };

  // Preview loads the file through the API (same as teachers), never from a storage link.
  const openPreview = async (resource) => {
    setPreview({ resource, loading: true });
    try {
      const isPdf = resource.fileType === "pdf";
      const response = await axios.get(`${baseUrl}/admin/resource/${resource.id}/content`, { responseType: isPdf ? "blob" : "text" });
      setPreview(isPdf
        ? { resource, url: URL.createObjectURL(new Blob([response.data], { type: "application/pdf" })) }
        : { resource, html: response.data });
    } catch (error) {
      handleAuthError(error);
      let message = "Could not load the resource";
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try { message = JSON.parse(await body.text())?.message || message; } catch { /* keep default */ }
      } else {
        message = errorMessage(error, message);
      }
      setPreview({ resource, error: message });
    }
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <DeleteModal show={!!toDelete} onDeleteClick={confirmDelete} onCloseClick={() => setToDelete(null)} />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Resource" />
          <Row className="align-items-center mb-3">
            <Col md={6}>
              <h5 className="card-title mb-0">
                Teacher Resources <span className="text-muted fw-normal ms-2">({resources.length})</span>
              </h5>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: 13 }}>
                PDF and HTML files teachers can open on the teacher site (view only, no download). They appear in the order below.
              </p>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                <Input type="text" placeholder="Search name..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 220 }} />
                <Button color="primary" onClick={openCreate}>Add Resource</Button>
              </div>
            </Col>
          </Row>

          {loadError && (
            <Alert color="danger" className="d-flex justify-content-between align-items-center">
              <span>{loadError}</span>
              <Button size="sm" color="danger" outline onClick={fetchResources}>Try again</Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center mt-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Added</th>
                    <th>Visible to teachers</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length > 0 ? (
                    visibleRows.map((resource) => (
                      <tr key={resource.id}>
                        <td>{resource.sortOrder}</td>
                        <td style={{ maxWidth: 320 }}>
                          <div className="fw-semibold">{resource.name}</div>
                          {resource.originalName && <div className="text-muted small text-truncate">{resource.originalName}</div>}
                        </td>
                        <td>
                          <Badge color={resource.fileType === "pdf" ? "danger" : "info"}>{String(resource.fileType || "").toUpperCase()}</Badge>
                        </td>
                        <td>{formatSize(resource.size)}</td>
                        <td>{formatDate(resource.createdAt)}</td>
                        <td>
                          <div className="form-check form-switch mb-0">
                            <Input type="switch" className="form-check-input" checked={isOn(resource.isVisible)} onChange={() => toggleVisible(resource)} />
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button size="sm" color="light" onClick={() => openPreview(resource)}>Preview</Button>
                            <Button size="sm" color="primary" outline onClick={() => openEdit(resource)}>Edit</Button>
                            <Button size="sm" color="danger" outline onClick={() => setToDelete(resource)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {search ? "No resources match your search." : "No resources yet. Click \"Add Resource\" to upload the first one."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>{editing ? "Edit Resource" : "Add Resource"}</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSave}>
            {saveError && <Alert color="danger">{saveError}</Alert>}
            <div className="mb-3">
              <Label className="form-label">Name</Label>
              <Input value={form.name} maxLength={200} placeholder="e.g. Teacher onboarding guide" invalid={!!errors.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <FormFeedback>{errors.name}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label className="form-label">Order</Label>
              <Input type="number" min={0} step={1} value={form.sortOrder} invalid={!!errors.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
              <FormFeedback>{errors.sortOrder}</FormFeedback>
              <small className="text-muted">Lower numbers show first on the teacher site.</small>
            </div>
            <div className="mb-3">
              <Label className="form-label">{editing ? "Replace file (optional)" : "File"}</Label>
              <Input type="file" accept={ACCEPT} invalid={!!errors.file} onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
              <FormFeedback>{errors.file}</FormFeedback>
              <small className="text-muted">
                PDF or HTML, up to {MAX_MB}MB. HTML files must be self-contained (images and styles inside the file or linked from the web).
                {editing && ` Current file: ${editing.originalName || editing.name} (${formatSize(editing.size)}).`}
              </small>
            </div>
            <div className="mb-3">
              <Label className="form-label d-block" id="resource-visible-label">Visible to teachers</Label>
              {/* Two plain buttons instead of a checkbox: the choice is always explicit. */}
              <div className="btn-group" role="radiogroup" aria-labelledby="resource-visible-label">
                <Button
                  type="button"
                  role="radio"
                  aria-checked={form.isVisible}
                  color={form.isVisible ? "success" : "light"}
                  onClick={() => setForm((prev) => ({ ...prev, isVisible: true }))}
                >
                  Visible
                </Button>
                <Button
                  type="button"
                  role="radio"
                  aria-checked={!form.isVisible}
                  color={!form.isVisible ? "secondary" : "light"}
                  onClick={() => setForm((prev) => ({ ...prev, isVisible: false }))}
                >
                  Hidden
                </Button>
              </div>
              <small className="d-block text-muted mt-1">
                {form.isVisible ? "Teachers will see this resource in their list." : "Teachers won't see this resource until you make it visible."}
              </small>
            </div>
            {saving && form.file && <Progress className="mb-3" value={progress}>{progress < 100 ? `${progress}%` : "Processing…"}</Progress>}
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button type="submit" color="primary" disabled={saving}>{saving ? "Saving…" : editing ? "Save changes" : "Add Resource"}</Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={!!preview} toggle={() => setPreview(null)} size="xl" centered>
        <ModalHeader toggle={() => setPreview(null)}>{preview?.resource?.name}</ModalHeader>
        <ModalBody style={{ height: "75vh", padding: 0 }}>
          {preview?.loading && (
            <div className="text-center mt-5"><div className="spinner-border text-primary" role="status" /></div>
          )}
          {preview?.error && <Alert color="danger" className="m-3">{preview.error}</Alert>}
          {preview?.url && <iframe title="Resource preview" src={preview.url} style={{ width: "100%", height: "100%", border: 0 }} />}
          {preview?.html !== undefined && (
            <iframe title="Resource preview" srcDoc={prepareHtmlForPreview(preview.html)} sandbox={HTML_SANDBOX} style={{ width: "100%", height: "100%", border: 0, background: "#fff" }} />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withAuth(ManageResource);
